import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  createdAt: number;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  private makeId() { return Math.random().toString(36).slice(2, 9); }

  show(message: string, type: Notification['type'] = 'info', ttl = 5000) {
    const n: Notification = { id: this.makeId(), message, type, createdAt: Date.now() };
    const list = [n, ...this.notificationsSubject.value];
    this.notificationsSubject.next(list);
    if (ttl > 0) {
      setTimeout(() => this.remove(n.id), ttl);
    }
    return n.id;
  }

  showSuccess(message: string, ttl = 5000) { return this.show(message, 'success', ttl); }
  showError(message: string, ttl = 7000) { return this.show(message, 'error', ttl); }
  showInfo(message: string, ttl = 5000) { return this.show(message, 'info', ttl); }
  showWarning(message: string, ttl = 6000) { return this.show(message, 'warning', ttl); }

  // Aliases for convenience
  success(message: string, ttl = 5000) { return this.showSuccess(message, ttl); }
  error(message: string, ttl = 7000) { return this.showError(message, ttl); }
  info(message: string, ttl = 5000) { return this.showInfo(message, ttl); }
  warning(message: string, ttl = 6000) { return this.showWarning(message, ttl); }

  remove(id: string) {
    const next = this.notificationsSubject.value.filter(n => n.id !== id);
    this.notificationsSubject.next(next);
  }

  clear() { this.notificationsSubject.next([]); }
}
